import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useRomaWebApi, type UserPreferences } from '../Api/RomaWebApi';
import { useAuthorization } from '../Auth/AuthorizationProvider';

const defaultPreferences: UserPreferences = {
    notifyOnMention: true,
    notifyOnAssignedChannel: true,
    notifyOnQuotedReply: true,
    notifyOnUnmutedChannel: true,
    mutedChannelIds: []
};

interface UserPreferencesContextValue {
    preferences: UserPreferences;
    updatePreferences: (prefs: UserPreferences) => Promise<UserPreferences>;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue>({
    preferences: defaultPreferences,
    updatePreferences: async (p) => p
});

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
    const api = useRomaWebApi();
    const { isAuthorized } = useAuthorization();
    const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
    const loaded = useRef(false);

    const prefsRef = useRef<UserPreferences>(defaultPreferences);

    const normalize = (p: Partial<UserPreferences>): UserPreferences => ({
        ...defaultPreferences,
        ...p,
        mutedChannelIds: Array.isArray(p.mutedChannelIds) ? p.mutedChannelIds : []
    });

    useEffect(() => {
        if (!isAuthorized) {
            loaded.current = false;
            setPreferences(defaultPreferences);
            prefsRef.current = defaultPreferences;
            return;
        }
        if (loaded.current) return;

        loaded.current = true;
        api.getUserPreferences().then(p => {
            const merged = normalize(p);
            setPreferences(merged);
            prefsRef.current = merged;
        }).catch(() => {});
    }, [api, isAuthorized]);

    const updatePreferences = useCallback(async (prefs: UserPreferences) => {
        const previous = prefsRef.current;
        const merged = normalize(prefs);
        setPreferences(merged);
        prefsRef.current = merged;
        try {
            const saved = await api.updateUserPreferences(merged);
            const final = normalize(saved);
            setPreferences(final);
            prefsRef.current = final;
            return final;
        } catch (err) {
            console.error('Failed to save preferences:', err);
            setPreferences(previous);
            prefsRef.current = previous;
            throw err;
        }
    }, [api]);

    return (
        <UserPreferencesContext.Provider value={{ preferences, updatePreferences }}>
            {children}
        </UserPreferencesContext.Provider>
    );
}

export function useUserPreferences() {
    return useContext(UserPreferencesContext);
}
